import { type ReactNode, useState } from "react";
import type { CSSProperties } from "react";
import type { GrupoRegistro, LigaRegistro, RedRegistro as DatosRed, ResumenRegistro, SegunOfac } from "../api.ts";
import { C, fecha, st } from "../theme.ts";

/**
 * Registro de comercio de una entidad OFAC, leído de la corrida vigente de Claude
 * (nexo_viz_entidad_red_registro, migración 20261001170000). Sustituye al árbol del
 * extractor anterior. Las frases las arma la base (un solo dueño); aquí sólo se muestran.
 * Nada se recorta en silencio: el pie dice cuántas ligas hay, cuántas se ven y qué se excluyó.
 */

const LIGAS_VISIBLES = 25;

function plural(n: number, uno: string, varios: string): string {
  return `${String(n)} ${n === 1 ? uno : varios}`;
}

/** Texto del distintivo (Explorador y encabezado de la Ficha): lo arma la base (un solo dueño). Etapa 4: sin la copia de respaldo. */
export function textoDistintivo(r: ResumenRegistro): string {
  return r.distintivo;
}

/** Lo que se lee al pasar el cursor: la liga más fuerte, cómo se identificó y, si es posible, qué hacer. */
export function detalleDistintivo(r: ResumenRegistro): string {
  return [r.frase, r.identidadTexto, r.recomendacion].filter((x): x is string => Boolean(x)).join("\n\n");
}

/** Revisión 4d (M2): una red cuya identidad no está corroborada se lee como posible (ámbar), igual que la base la dice. */
export const registroPosible = (r: ResumenRegistro): boolean => r.estado === "posible" || (r.estado === "red" && r.corroborada === false);

export function DistintivoRegistro({ resumen, onClick }: { resumen: ResumenRegistro; onClick?: () => void }) {
  const posible = registroPosible(resumen);
  const color = posible ? C.amber : C.tiel;
  const estilo: CSSProperties = {
    fontSize: 11, fontWeight: 700, color, background: posible ? C.amberBg : "transparent",
    border: `1px solid ${color}`, borderRadius: 999, padding: "1px 8px", whiteSpace: "normal",
    cursor: onClick ? "pointer" : "default", textAlign: "left",
  };
  return onClick ? (
    <button type="button" title={detalleDistintivo(resumen)} onClick={onClick} style={estilo}>{textoDistintivo(resumen)}</button>
  ) : (
    <span title={detalleDistintivo(resumen)} style={estilo}>{textoDistintivo(resumen)}</span>
  );
}

function Evidencia({ liga }: { liga: LigaRegistro }) {
  const citas = (liga.camino ?? []).flatMap((t) => t.evidencia.map((ev) => ({ ...ev })));
  if (citas.length === 0) return null;
  return (
    <details style={{ marginTop: 4 }}>
      <summary style={{ fontSize: 12, color: C.muted, cursor: "pointer" }}>
        Ver evidencia ({plural(citas.length, "cita", "citas")})
      </summary>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        {citas.map((ev, i) => (
          <li key={i} style={{ fontSize: 12, color: C.ink, marginBottom: 6 }}>
            <span style={{ color: C.muted }}>
              {[ev.archivo, ev.pag != null ? `página ${String(ev.pag)}` : null, ev.fecha ? `acto de ${ev.fecha}` : null]
                .filter(Boolean).join(" · ")}
              {ev.cita_verificada === false ? " · cita no verificada contra el PDF" : ""}
            </span>
            {ev.cita ? <blockquote style={{ margin: "2px 0 0", paddingLeft: 8, borderLeft: `2px solid ${C.line}`, fontStyle: "italic" }}>{ev.cita}</blockquote> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

function Grupo({ grupo }: { grupo: GrupoRegistro }) {
  const [todas, setTodas] = useState(false);
  const visibles = todas ? grupo.ligas : grupo.ligas.slice(0, LIGAS_VISIBLES);
  return (
    <section style={{ marginTop: 14 }}>
      <h3 style={{ fontSize: 14, margin: "0 0 6px", color: C.ink }} title={grupo.explicacion ?? undefined}>
        {grupo.titulo} <span style={{ color: C.muted, fontWeight: 400 }}>({String(grupo.ligas.length)})</span>
      </h3>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {visibles.map((l) => (
          <li key={`${l.actorId}-${l.tipoLiga ?? "registro"}`} style={{ padding: "8px 0", borderTop: `1px solid ${C.line}` }}>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, color: C.ink }}>{l.frase}</p>
            <Evidencia liga={l} />
          </li>
        ))}
      </ul>
      {grupo.ligas.length > LIGAS_VISIBLES ? (
        <button
          type="button"
          onClick={() => { setTodas(!todas); }}
          style={{ marginTop: 6, fontSize: 12, color: C.tiel, background: "transparent", border: `1px solid ${C.tiel}`, borderRadius: 999, padding: "2px 10px", cursor: "pointer" }}
        >
          {todas ? "Ver menos" : `Ver las ${String(grupo.ligas.length)} relaciones de este grupo`}
        </button>
      ) : null}
    </section>
  );
}

/** Lo que OFAC declara de la entidad, con la dirección que registra OFAC (#167). */
export function SegunOfacSeccion({ datos }: { datos: SegunOfac }) {
  if (datos.total === 0) return null;
  return (
    <div style={{ ...st.tarjeta, marginTop: 12 }}>
      <p style={st.eyebrow}>Según OFAC</p>
      <ul style={{ listStyle: "none", margin: "4px 0 0", padding: 0 }}>
        {datos.relaciones.map((r, i) => (
          <li key={i} style={{ padding: "6px 0", borderTop: `1px solid ${C.line}`, fontSize: 13.5, color: C.ink }}>
            {r.frase}
            {r.programas.length > 0 ? <span style={{ color: C.muted, fontSize: 12 }}> · programas de la otra parte: {r.programas.join(", ")}</span> : null}
          </li>
        ))}
      </ul>
      <p style={{ margin: "8px 0 0", fontSize: 11.5, color: C.muted }}>
        {datos.mostradas < datos.total ? `Se muestran ${String(datos.mostradas)} de ${String(datos.total)} relaciones. ` : ""}
        Fuente: {datos.fuente}{datos.corte ? `, corte ${datos.corte.slice(0, 10).split("-").reverse().join("-")}` : ""} (fecha de nuestra carga, no fecha jurídica de la relación).
      </p>
    </div>
  );
}

/**
 * `dibujo`: el dibujo de la red va dentro de la misma sección, entre el resumen y las frases (Bernardo, 2026-10-01).
 * `sinRespuesta` (etapa 4): con la ficha nueva pintada arriba, se ocultan el distintivo y el «Qué hacer» de aquí, para que no convivan
 * dos respuestas (decisión del coordinador con el precedente de Argus Team: «ocultar lo viejo» cuando está la ficha nueva).
 */
export function RedRegistro({ datos, dibujo, sinRespuesta = false }: { datos: DatosRed; dibujo?: ReactNode; sinRespuesta?: boolean }) {
  const r = datos.resumen;
  if (!r) return null;
  const t = datos.totales;
  return (
    <div style={{ ...st.tarjeta, marginTop: 12 }}>
      <p style={st.eyebrow}>Registro Público de Comercio</p>
      {sinRespuesta ? null : (
        <p style={{ margin: "2px 0 6px", fontSize: 15, fontWeight: 700, color: registroPosible(r) ? C.amber : C.ink }}>{textoDistintivo(r)}</p>
      )}
      <p style={{ margin: 0, fontSize: 13, color: C.muted }}>{r.identidadTexto}</p>
      {r.recomendacion && !sinRespuesta ? (
        <p style={{ margin: "10px 0 0", fontSize: 13, color: C.ink, background: C.amberBg, border: `1px solid ${C.amber}`, borderRadius: 8, padding: "8px 10px" }}>
          <strong>Qué hacer: </strong>{r.recomendacion}
        </p>
      ) : null}
      {dibujo}
      {datos.grupos.map((g) => <Grupo key={g.clave} grupo={g} />)}
      <p style={{ margin: "14px 0 0", fontSize: 12, color: C.muted }}>
        {t.ligas === 0
          ? null
          : t.omitidas === 0
            ? (t.mostradas === 1 ? "Se muestra la única relación; ninguna omitida." : `Se muestran las ${String(t.mostradas)} relaciones; ninguna omitida.`)
            : `Se muestran ${String(t.mostradas)} de ${plural(t.ligas, "relación", "relaciones")}: ${t.motivoOmision ?? "límite de tamaño"}.`}
        {t.excluidas > 0
          ? t.excluidas === 1
            ? " No se cuenta 1 camino que pasa por un notario, auxiliar o autoridad: no es exposición."
            : ` No se cuentan ${String(t.excluidas)} caminos que pasan por un notario, auxiliar o autoridad: no son exposición.`
          : ""}
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 11.5, color: C.muted }}>
        {datos.aviso}
        {datos.run?.corteOfac ? ` Corte OFAC: ${fecha(datos.run.corteOfac)}.` : ""}
      </p>
    </div>
  );
}
